/**
 * Specific rendering for IA answers and questions.
 */
import React from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import ShowMoreButton from "../MoreButton";
import { BackendModuleTypes } from "../../lib/types";
import { iaGoToModule } from "./actions";
import { Answer } from "../messages/Answer";
import { getLatestState } from "../../store";
import { getKnownBackendModules } from "./selectors";

/**
 * Renders the list (`ul` tag) of items with a functional "show more" button.
 * @param items
 * @returns {*}
 * @constructor
 */
const ListMessage = ({ data: items }) => {
  const [shouldShowMore, setShouldShowMore] = React.useState(false);

  const maxOptionsToInitiallyShow = 6;
  let itemsToRender = items;
  let showMoreButton = null;

  if (items.length > maxOptionsToInitiallyShow && !shouldShowMore) {
    itemsToRender = items.slice(0, maxOptionsToInitiallyShow);
    showMoreButton = (
      <ShowMoreButton className="small" onClick={setShouldShowMore}>
        More
      </ShowMoreButton>
    );
  }

  return (
    <>
      <ul style={{ listStyleType: "disc", padding: "0.5rem" }}>
        {itemsToRender.map((v, i) => (
          <li key={i}>{v}</li>
        ))}
      </ul>
      {showMoreButton}
    </>
  );
};

/**
 * How the blocks of every type should be rendered.
 */
const blocks = {
  text: ({ data }) => <p>{data}</p>,
  list: ({ data }) => <ListMessage data={data} />,
};

/**
 * Render IA messaage content.
 * @param values - array of objects to render.
 * @param dry - when `true` - only returns true/false if anything will be rendering in a `wet` run.
 * @returns {*}
 * @constructor
 */
export const IAMessageContent = ({ values, dry = false }) => {
  const _values = values.filter((v) => v.type in blocks).filter((v) => !!v.data);

  // Need to check if anything will be rendered here later.
  if (dry) {
    return _values?.length > 0;
  }

  return _values?.length > 0 ? (
    <>
      {_values?.map((v, i) => {
        const Cmp = blocks[v.type];
        return <Cmp data={v.data} key={i} />;
      })}
    </>
  ) : null;
};

/**
 * IA Action component
 * @param action - string from `BackendModuleTypes`.
 * @constructor
 */
export const IAaction = ({ action }) => {
  const dispatch = useDispatch();
  const state = dispatch(getLatestState);
  let buttonText;

  const knownModules = Object.keys(getKnownBackendModules(state));

  const onAnswerClick = () => dispatch(iaGoToModule(action));

  if (!knownModules.includes(action)) {
    return <Answer onClick={onAnswerClick}>Continue</Answer>;
  }

  switch (action) {
    case BackendModuleTypes.OPEN_GALLERY:
      buttonText = "View Images";
      break;
    case BackendModuleTypes.ANSWER_FLOW:
      buttonText = "Ask Us";
      break;
    case BackendModuleTypes.OPEN_FLOORPLAN:
      buttonText = "View Floor Plans";
      break;
    case BackendModuleTypes.OPEN_PRICING:
      buttonText = "Get Pricing";
      break;
    case BackendModuleTypes.OPEN_SCHEDULE_TOUR:
      buttonText = "Schedule tour";
      break;
    default:
      buttonText = "Continue";
      break;
  }

  return <Answer onClick={onAnswerClick}>{buttonText}</Answer>;
};

/**
 * Required to wrap text suggestions via additional div to deal with the chat's flex display.
 */
export const SuggestionsWrapper = styled.div`
  width: 100%;
  padding: 0 20px;
`;

export const Suggestion = styled.div`
  cursor: pointer;
  color: ${(props) => props.theme.brandColor};
  font-size: ${(props) => props.theme.fontSize};
  border: 1px solid ${(props) => props.theme.brandColor};
  text-align: left;
  border-radius: 4px;

  padding: 5px 9px;
  margin: 5px 0;

  max-width: 100%;
  width: fit-content;

  transition: box-shadow 0.2s;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    box-shadow: 0 2px 4px rgba(${(props) => props.theme.brandColorRGB}, 0.5);
    text-overflow: clip;
    white-space: normal;
    word-break: break-word;
  }
`;
